<template>
  <div
    class="modal ledger-entry-modal"
    @keydown="handleModalKeyDown"
    @keyup.stop
    @keypress.stop
    @click.stop
    @mousedown.stop
    @mouseup.stop
  >
    <div class="modal-content">
      <div v-if="isClosing" class="save-busy-overlay" aria-live="polite">
        <i class="fas fa-spinner fa-spin save-busy-spinner"></i>
        <div class="save-busy-label">Saving changes before closing…</div>
      </div>
      <div class="modal-header">
        <h2>
          Add or Edit Ledger Entry
          <span>{{ isSplitMode ? `(split : ${siblings.length})` : '' }}</span>
        </h2>
        <button class="print-btn" @click="emitPrintCheck" :disabled="isClosing">
          <i class="fas fa-print"></i>
        </button>
        <button
          class="close-btn"
          @click="hideModal"
          :disabled="isClosing"
          :title="isClosing ? 'Saving…' : 'Close'"
        >
          <i v-if="isClosing" class="fas fa-spinner fa-spin"></i>
          <i v-else class="fas fa-close"></i>
        </button>
      </div>
      <div class="table">
        <!-- Table Header -->
        <div class="table-header">
          <div class="col date">Date</div>
          <div class="col type">Type</div>
          <div class="col num">Num</div>
          <div class="col amount">Amount</div>
          <div class="col name">Name</div>
          <div class="col memo">Memo</div>
          <div class="col account">Account</div>
          <div class="col split">Split/Bank Acct</div>
          <div class="col class">Class</div>
        </div>

        <!-- ========== SPLIT MODE: spreadsheet-style in-place rows ========== -->
        <template v-if="isSplitMode">
          <!-- Parent row (editable in place) -->
          <div
            class="parent-row spreadsheet-row"
            :class="{
              'selected-sibling-row': selectedRow?.index === parentRowIndex,
              'row-save-pending': parentEntry._savePending,
              'row-save-error': parentEntry._saveError,
            }"
            @click="selectedRow = { ...parentEntry, index: parentRowIndex }"
          >
            <div class="col date cell" :title="parentEntry.Id">
              <input
                type="text"
                class="cell-input"
                v-model="parentEntry.Date"
                placeholder="mm/dd/yy"
                @focus="setActiveEdit('parent', null, 'Date', $event)"
                @blur="onParentBlur('Date')"
                @keydown.tab="preventShiftTabWrapFromFirst"
              />
            </div>
            <div class="col type cell">
              <input
                type="text"
                class="cell-input"
                v-model="parentEntry.Type"
                @focus="setActiveEdit('parent', null, 'Type', $event)"
                @blur="onParentBlur('Type')"
              />
            </div>
            <div class="col num cell">
              <input
                type="text"
                class="cell-input"
                v-model="parentEntry.Num"
                @focus="setActiveEdit('parent', null, 'Num', $event)"
                @blur="onParentNumBlur"
              />
            </div>
            <div
              class="col amount cell amount-readonly amount-with-status"
              title="Sum of split lines"
            >
              {{ formatAmount(-totalSiblingAmount) }}
              <span
                v-if="parentEntry._savePending"
                class="row-save-status pending"
                title="Parent total still saving…"
              >
                <i class="fas fa-spinner fa-spin"></i>
              </span>
              <span
                v-else-if="parentEntry._saveError"
                class="row-save-status error"
                title="Parent save failed — click Save to retry"
              >
                <i class="fas fa-exclamation-circle"></i>
              </span>
            </div>
            <div class="col name cell">
              <input
                type="text"
                class="cell-input"
                :value="parentEntry.Name"
                :title="parentEntry.Name"
                @focus="setActiveEdit('parent', null, 'Name', $event)"
                @input="onSpreadsheetNameInput($event, 'parent')"
                @keydown.arrow-down.prevent="handleNameArrowDown"
                @blur="onParentBlur('Name')"
              />
            </div>
            <div class="col memo cell">
              <input
                type="text"
                class="cell-input"
                :value="parentEntry.Memo"
                :title="parentEntry.Memo"
                @focus="setActiveEdit('parent', null, 'Memo', $event)"
                @input="onSpreadsheetMemoInput($event, 'parent')"
                @keydown.arrow-down.prevent="handleMemoArrowDown"
                @blur="onParentBlur('Memo')"
              />
            </div>
            <div class="col account cell">
              <input
                type="text"
                class="cell-input"
                :value="
                  activeEdit?.kind === 'parent' && activeEdit?.field === 'Account'
                    ? accountQuery
                    : parentEntry.Account
                "
                :title="parentEntry.Account"
                @focus="setActiveEdit('parent', null, 'Account', $event)"
                @input="onSpreadsheetAccountInput($event, 'parent')"
                @keydown.arrow-down.prevent="handleAccountArrowDown"
                @blur="onParentBlur('Account')"
              />
            </div>
            <div class="col split cell amount-readonly">-SPLIT-</div>
            <div class="col class cell amount-readonly"></div>
          </div>

          <!-- Child rows (editable in place) -->
          <div class="siblings-outer">
            <div class="siblings-inner" @scroll="onAutocompleteScroll">
              <div
                v-for="(sibling, index) in siblings"
                :key="sibling._rowKey"
                class="sibling-row spreadsheet-row"
                :class="{
                  'selected-sibling-row': selectedRow?.index === index,
                  'row-save-pending': sibling._savePending,
                  'row-save-error': sibling._saveError,
                }"
                @click="selectedRow = { ...sibling, index }"
              >
                <div class="col date cell amount-readonly" :title="sibling.Id">
                  {{ formatDate(sibling.Date || parentEntry.Date) }}
                </div>
                <div class="col type cell amount-readonly"></div>
                <div class="col num cell amount-readonly"></div>
                <div class="col amount cell amount-with-status">
                  <input
                    type="text"
                    class="cell-input amount-input"
                    title="examples: 1+2+3, 15-10, 2*10"
                    v-model="sibling.Amount"
                    @focus="setActiveEdit('sibling', index, 'Amount', $event)"
                    @blur="onSiblingAmountBlur(index, $event)"
                  />
                  <span
                    v-if="sibling._savePending"
                    class="row-save-status pending"
                    title="This line is still saving…"
                  >
                    <i class="fas fa-spinner fa-spin"></i>
                  </span>
                  <span
                    v-else-if="sibling._saveError"
                    class="row-save-status error"
                    title="Save failed — edit a cell or click Save to retry"
                  >
                    <i class="fas fa-exclamation-circle"></i>
                  </span>
                  <span
                    v-else-if="sibling.Id && sibling.Id !== -1 && !sibling._dirty"
                    class="row-save-status saved"
                    title="Saved to database"
                  >
                    <i class="fas fa-check"></i>
                  </span>
                </div>
                <div class="col name cell amount-readonly"></div>
                <div class="col memo cell">
                  <input
                    type="text"
                    class="cell-input"
                    :value="sibling.Memo"
                    :title="sibling.Memo"
                    @focus="setActiveEdit('sibling', index, 'Memo', $event)"
                    @input="onSpreadsheetMemoInput($event, 'sibling', index)"
                    @keydown.arrow-down.prevent="handleMemoArrowDown"
                    @blur="onSiblingBlur(index, 'Memo')"
                  />
                </div>
                <div class="col account cell">
                  <input
                    type="text"
                    class="cell-input"
                    :value="
                      activeEdit?.kind === 'sibling' &&
                      activeEdit?.index === index &&
                      activeEdit?.field === 'Account'
                        ? accountQuery
                        : sibling.Account
                    "
                    :title="sibling.Account"
                    @focus="setActiveEdit('sibling', index, 'Account', $event)"
                    @input="onSpreadsheetAccountInput($event, 'sibling', index)"
                    @keydown.arrow-down.prevent="handleAccountArrowDown"
                    @blur="onSiblingBlur(index, 'Account')"
                  />
                </div>
                <div class="col split cell amount-readonly" :title="parentEntry.Account">
                  {{ shortAccount(parentEntry.Account) }}
                </div>
                <div class="col class cell">
                  <input
                    type="text"
                    class="cell-input"
                    :value="
                      activeEdit?.kind === 'sibling' &&
                      activeEdit?.index === index &&
                      activeEdit?.field === 'Class'
                        ? classQuery
                        : sibling.Class
                    "
                    :title="sibling.Class"
                    @focus="setActiveEdit('sibling', index, 'Class', $event)"
                    @input="onSpreadsheetClassInput($event, 'sibling', index)"
                    @keydown.tab="handleSiblingClassTabKey($event, index)"
                    @keydown.arrow-down.prevent="handleClassArrowDown"
                    @blur="onSiblingBlur(index, 'Class')"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- ========== NON-SPLIT: single input row (unchanged flow) ========== -->
        <div v-else class="input-row">
          <div class="col date" style="text-overflow: unset; border: 0">
            <input
              class="col date special"
              type="text"
              ref="dateInput"
              v-model="currentInput.Date"
              placeholder="mm/dd/yy"
              @blur="onSingleFieldBlur('Date')"
              @keydown.tab="handleSingleModeTabKey($event, 'Date')"
              @keyup.enter="handleEnter"
              @focus="setActiveEdit('single', null, 'Date', $event)"
            />
          </div>
          <div class="col type" style="text-overflow: unset; border: 0">
            <input
              class="col type special"
              ref="typeInput"
              type="text"
              v-model="currentInput.Type"
              @blur="onSingleFieldBlur('Type')"
              @keydown.tab="handleSingleModeTabKey($event, 'Type')"
              @keyup.enter="handleEnter"
              @focus="setActiveEdit('single', null, 'Type', $event)"
            />
          </div>

          <div class="col num" style="text-overflow: unset; border: 0">
            <input
              class="col num special"
              type="text"
              ref="numInput"
              v-model="currentInput.Num"
              @blur="onSingleNumBlur"
              @keydown.tab="handleSingleModeTabKey($event, 'Num')"
              @keyup.enter="handleEnter"
              @focus="setActiveEdit('single', null, 'Num', $event)"
            />
          </div>

          <div class="col amount" style="text-overflow: unset; border: 0">
            <input
              :placeholder="'math or #'"
              title="examples: 1+2+3, 15-10, 2*10"
              ref="amountInput"
              class="col amount special"
              type="text"
              v-model="currentInput.Amount"
              @keyup.enter="handleEnter"
              @keydown.tab="handleSingleModeTabKey($event, 'Amount')"
              @blur="onSingleAmountBlur"
              @focus="setActiveEdit('single', null, 'Amount', $event)"
            />
          </div>

          <div class="col name" style="text-overflow: unset; border: 0">
            <input
              class="col name special"
              type="text"
              ref="nameInput"
              :value="nameQuery"
              :title="nameQuery"
              @blur="onSingleFieldBlur('Name')"
              @input="handleNameInput"
              @keydown.tab="handleSingleModeTabKey($event, 'Name')"
              @keydown.arrow-down.prevent="handleNameArrowDown"
              @keyup.enter="handleEnter"
              @focus="setActiveEdit('single', null, 'Name', $event)"
            />
          </div>

          <div class="col memo" style="text-overflow: unset; border: 0">
            <input
              placeholder="add a new entry here..."
              class="col memo special"
              type="text"
              ref="memoInput"
              :value="memoQuery"
              :title="memoQuery"
              @blur="onSingleFieldBlur('Memo')"
              @input="handleMemoInput"
              @keyup.enter="handleEnter"
              @keydown.tab="handleSingleModeTabKey($event, 'Memo')"
              @keydown.arrow-down.prevent="handleMemoArrowDown"
              @focus="setActiveEdit('single', null, 'Memo', $event)"
            />
          </div>

          <div class="col account" style="text-overflow: unset; border: 0">
            <input
              class="col account special"
              type="text"
              ref="accountInput"
              :value="accountQuery"
              :title="accountQuery"
              @blur="onSingleFieldBlur('Account')"
              @input="handleAccountInput"
              @keydown.tab="handleSingleModeTabKey($event, 'Account')"
              @keydown.arrow-down.prevent="handleAccountArrowDown"
              @keyup.enter="handleEnter"
              @focus="setActiveEdit('single', null, 'Account', $event)"
            />
          </div>

          <div class="col split" style="text-overflow: unset; border: 0">
            <input
              class="col split special"
              type="text"
              ref="splitInput"
              :value="splitQuery"
              :title="splitQuery"
              @blur="onSingleFieldBlur('Split')"
              @input="handleSplitInput"
              @keydown.tab="handleSingleModeTabKey($event, 'Split')"
              @keydown.arrow-down.prevent="handleSplitArrowDown"
              @keyup.enter="handleEnter"
              @focus="setActiveEdit('single', null, 'Split', $event)"
            />
          </div>

          <div class="col class" style="text-overflow: unset; border: 0">
            <input
              class="col class special"
              type="text"
              ref="classInput"
              :value="classQuery"
              :title="classQuery"
              @input="handleClassInput"
              @keydown.tab="handleSingleModeTabKey($event, 'Class')"
              @keydown.arrow-down.prevent="handleClassArrowDown"
              @keyup.enter="handleEnter"
              @blur="onSingleFieldBlur('Class')"
              @focus="setActiveEdit('single', null, 'Class', $event)"
            />
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button @click="addSibling">+ Split</button>
        <button @click="swapAccountAndSplit">
          <i class="fas fa-exchange-alt"></i> Swap
        </button>
        <button @click="saveEntry">Save</button>
        <button
          :class="selectedRow?.index != null ? 'emphasize-button' : ''"
          @click="deleteEntry"
        >
          Delete
        </button>
        <button
          @click="duplicateEntry"
          title="Duplicate this entry with check number PRINT"
        >
          <i class="fas fa-copy"></i> Dup
        </button>
        <button
          class="refresh-btn"
          @click="refreshAutocompleteData"
          title="Refresh autocomplete data"
        >
          <i class="fas fa-sync-alt"></i>
        </button>
      </div>
      <!--
        Single fixed-height status strip for pending / saved / error.
        Never mount/unmount or v-show:false — that was resizing the dialog.
      -->
      <div class="ledger-status-slot" aria-live="polite">
        <div
          class="ledger-status-inner"
          :class="ledgerStatusClass"
          :aria-hidden="ledgerStatusEmpty ? 'true' : 'false'"
        >
          <template v-if="pendingSaveCount > 0">
            <i class="fas fa-spinner fa-spin"></i>
            <span
              >Saving {{ pendingSaveCount }} line{{
                pendingSaveCount === 1 ? '' : 's'
              }}… (you can keep editing)</span
            >
          </template>
          <template v-else-if="errorMessage">
            <span>{{ errorMessage }}</span>
          </template>
        </div>
      </div>
    </div>
  </div>

  <CustomModal
    :visible="showModal"
    :title="modalContent.title"
    :message="modalContent.message"
    @confirm="handleModalConfirm(modalContent.actionType)"
    @cancel="hideModal"
  />

  <!-- Escape overflow clipping (sibling scroll + footer buttons) -->
  <Teleport to="body">
    <div
      v-if="portalDropdownOpen"
      class="dropdown dropdown-portal"
      :class="portalDropdownExtraClass"
      :style="dropdownFixedStyle"
    >
      <div
        v-for="(match, index) in portalMatches"
        :key="index"
        class="dropdown-item"
        @mousedown.prevent="selectMatch(match.value, portalDropdownField)"
      >
        <span v-html="match.highlighted"></span>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import {
  ref,
  reactive,
  computed,
  onMounted,
  onBeforeUnmount,
  defineProps,
  nextTick,
} from 'vue';
import axios from '@/axiosConfig';
import CustomModal from './CustomModal.vue'; // Adjust path if necessary
import eventBus from './eventBus.js'; // Import eventBus
import {
  setMruUserId,
  resolveUserIdFromToken,
  recordFieldUse,
  rankMatches,
  classSecondaryCompare,
  MRU_CATEGORIES,
} from '@/utils/autocompleteMru';
import { ledgerSaveAndCloseKey } from '@/utils/ledgerEntryKeys';
import { PRINT_CHECK_NUMBER } from '@/utils/printCheckNumber';

// Scope MRU lists to the signed-in user (localStorage)
setMruUserId(resolveUserIdFromToken());

// custom modal
const showModal = ref(false);

const modalContent = reactive({
  title: '',
  message: '',
  actionType: '',
});

// Define props
const props = defineProps({
  id: { type: Number, required: false },
  account: { type: String, required: false },
  isNew: { type: Boolean, default: false }, // New prop to indicate a new row
  data: { type: Object, required: false }, // New prop to accept data object
});

// URL and current ID management
const query = new URLSearchParams(window.location.search);
const urlId = query.get('id');
const currentId = urlId || props.id;

// Reactive data definitions
const currentInput = reactive({
  Id: null,
  Date: '',
  Num: '',
  Type: '',
  Amount: '',
  Name: '',
  Memo: '',
  Split: '',
  Account: props.account || '',
  Class: '',
});

const parentEntry = ref({});
const siblings = ref([]);
const selectedRow = ref(null);
const classes = ref([]);
const memos = ref([]);
const names = ref([]);
const chartOfAccounts = ref([]);
const isSplitMode = ref(false);
/** Which cell is being edited: { kind: 'parent'|'sibling'|'single', index, field } */
const activeEdit = ref(null);
const isSaving = ref(false);
/** User clicked close (X); spinner only shows then while draining the save queue. */
const isClosing = ref(false);
const lastSaveMessageAt = ref(0);
/**
 * Blur-save debounce: delay so autocomplete mousedown can apply first.
 * IMPORTANT: when a new blur-save is scheduled, flush any pending one into the
 * save queue instead of dropping it — rapid paste across child rows was only
 * persisting the last cell edited.
 */
let blurSaveTimer = null;
let pendingBlurSave = null;
/** Serialize all ledger writes so none are skipped while isSaving is true */
let saveChain = Promise.resolve();
const SAVE_LOCK_WAIT_MS = 45000;
const CLOSE_FLUSH_MS = 45000;
const LEDGER_AXIOS_TIMEOUT_MS = 30000;

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Wait until isSaving is clear, or force-clear after timeout so the queue
 * cannot deadlock on a hung network call (was: infinite while-loop).
 */
async function waitForSaveLockClear(maxMs = SAVE_LOCK_WAIT_MS) {
  const start = Date.now();
  while (isSaving.value) {
    if (Date.now() - start > maxMs) {
      console.warn(
        '[LedgerEntry] isSaving stuck true; force-clearing to unblock save queue',
      );
      isSaving.value = false;
      break;
    }
    await sleep(25);
  }
}

/**
 * Await the save chain with a hard timeout. On timeout, drop the stuck chain
 * so Close cannot hang forever on "Saving changes before closing…".
 */
async function awaitSaveChain(maxMs = CLOSE_FLUSH_MS) {
  try {
    await Promise.race([
      saveChain,
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error('save-chain-timeout')), maxMs);
      }),
    ]);
  } catch (e) {
    if (e?.message === 'save-chain-timeout') {
      console.warn(
        '[LedgerEntry] save chain timed out; resetting queue so UI can proceed',
      );
      isSaving.value = false;
      saveChain = Promise.resolve();
      return false;
    }
    throw e;
  }
  return true;
}

function enqueueSave(fn) {
  // Tab/blur saves stay async and silent — no spinner (only isClosing shows UI).
  const run = async () => {
    await waitForSaveLockClear();
    return fn();
  };
  saveChain = saveChain.then(run, run);
  return saveChain;
}

/** Mark a split child as waiting on (or in) a network save. */
function markSiblingSavePending(index) {
  const sibling = siblings.value[index];
  if (!sibling) return;
  sibling._savePending = true;
  sibling._saveError = false;
}

function clearSiblingSavePending(index, { error = false } = {}) {
  const sibling = siblings.value[index];
  if (!sibling) return;
  sibling._savePending = false;
  sibling._saveError = !!error;
}

function markParentSavePending() {
  if (!parentEntry.value) return;
  parentEntry.value._savePending = true;
  parentEntry.value._saveError = false;
}

function clearParentSavePending({ error = false } = {}) {
  if (!parentEntry.value) return;
  parentEntry.value._savePending = false;
  parentEntry.value._saveError = !!error;
}

/**
 * Queue a child save and show the row “saving…” hint immediately (including
 * while the row is still waiting its turn in the save queue).
 * Default announce:true — used for Class Tab / completed-row saves.
 */
function enqueueSiblingSave(index, options = {}) {
  const opts = { announce: true, ...options };
  markSiblingSavePending(index);
  return enqueueSave(async () => {
    const ok = await persistSiblingRow(index, opts);
    // persistSiblingRow clears pending; re-apply if it returned early before mark
    if (!siblings.value[index]?._savePending && !ok) {
      clearSiblingSavePending(index, { error: true });
    }
    return ok;
  });
}

function flushPendingBlurSave() {
  if (blurSaveTimer) {
    clearTimeout(blurSaveTimer);
    blurSaveTimer = null;
  }
  if (pendingBlurSave) {
    const job = pendingBlurSave;
    pendingBlurSave = null;
    enqueueSave(job);
  }
}

function scheduleBlurSave(fn) {
  // Don't drop the previous row's save when focus moves quickly
  if (pendingBlurSave) {
    flushPendingBlurSave();
  } else if (blurSaveTimer) {
    clearTimeout(blurSaveTimer);
    blurSaveTimer = null;
  }
  pendingBlurSave = fn;
  blurSaveTimer = setTimeout(() => {
    blurSaveTimer = null;
    const job = pendingBlurSave;
    pendingBlurSave = null;
    if (job) enqueueSave(job);
  }, 180);
}

function cancelBlurSave() {
  if (blurSaveTimer) {
    clearTimeout(blurSaveTimer);
    blurSaveTimer = null;
  }
  pendingBlurSave = null;
}

/**
 * Parent amount is -sum(children). Set when a child amount changes; cleared
 * after persistParentRow. Close only re-saves parent when this is true.
 */
let parentAmountNeedsSync = false;

function markSiblingDirty(index) {
  const sibling = siblings.value[index];
  if (sibling) sibling._dirty = true;
}

/** True if this child needs a network write (edited, or new with content). */
function shouldPersistSibling(sibling) {
  if (!sibling) return false;
  if (sibling._dirty) return true;
  const isNew = !sibling.Id || sibling.Id === -1;
  if (!isNew) return false;
  const amt = parseFloat(sibling.Amount || 0);
  const hasText = !!(
    (sibling.Memo && String(sibling.Memo).trim()) ||
    (sibling.Account && String(sibling.Account).trim()) ||
    (sibling.Class && String(sibling.Class).trim())
  );
  return Math.abs(amt) > 0.0001 || hasText;
}

/**
 * Copy shared query strings onto the sibling currently being edited so a
 * Save / + Split / close does not lose the focused field.
 */
function commitActiveSiblingFromQueries() {
  const ae = activeEdit.value;
  if (!ae || ae.kind !== 'sibling') return;
  const sibling = siblings.value[ae.index];
  if (!sibling) return;

  if (ae.field === 'Memo') {
    sibling.Memo = memoQuery.value || sibling.Memo || '';
    sibling._dirty = true;
  } else if (ae.field === 'Account') {
    sibling.Account =
      accountMatches.value[0]?.value ||
      accountQuery.value ||
      sibling.Account ||
      '';
    sibling._dirty = true;
  } else if (ae.field === 'Class') {
    sibling.Class =
      classMatches.value[0]?.value || classQuery.value || sibling.Class || '';
    sibling._dirty = true;
  } else if (ae.field === 'Amount') {
    sibling.Amount = evaluateAmountExpression(sibling.Amount);
    sibling._dirty = true;
    parentAmountNeedsSync = true;
  }
}

/** Persist dirty / new-with-content children only; optionally sync parent total. */
async function persistDirtySiblings({ syncParentIfNeeded = true } = {}) {
  let saved = 0;
  let failed = 0;
  for (let i = 0; i < siblings.value.length; i++) {
    if (!shouldPersistSibling(siblings.value[i])) continue;
    try {
      const ok = await persistSiblingRow(i, { syncParent: false });
      if (ok) saved += 1;
      else failed += 1;
    } catch (e) {
      failed += 1;
      console.error(`[LedgerEntry] failed to save sibling index ${i}:`, e);
    }
  }
  if (syncParentIfNeeded && parentAmountNeedsSync && parentEntry.value?.Id) {
    try {
      await persistParentRow();
    } catch (e) {
      failed += 1;
      console.error('[LedgerEntry] failed to sync parent after children:', e);
    }
  }
  return { saved, failed };
}

async function flushAllPendingSaves() {
  flushPendingBlurSave();
  await awaitSaveChain(CLOSE_FLUSH_MS);
}

/**
 * Close path: finish debounced field save + in-flight queue, then dirty
 * children that never got a successful blur save. Always time-bounded so
 * the dialog cannot hang on "Saving changes before closing…".
 */
async function flushSavesOnClose() {
  flushPendingBlurSave();
  await awaitSaveChain(CLOSE_FLUSH_MS);

  if (isSplitMode.value) {
    // Capture dirty children that never got a successful blur save
    await enqueueSave(() =>
      persistDirtySiblings({ syncParentIfNeeded: true }),
    );
    await awaitSaveChain(CLOSE_FLUSH_MS);
  } else if (parentAmountNeedsSync && parentEntry.value?.Id) {
    await enqueueSave(() => persistParentRow());
    await awaitSaveChain(CLOSE_FLUSH_MS);
  }
}

const totalSiblingAmount = computed(() => {
  let total = siblings.value.reduce(
    (sum, sibling) => sum + parseFloat(sibling.Amount || 0),
    0,
  );

  return total;
});

/** Rows currently waiting on or running a background save (for footer banner). */
const pendingSaveCount = computed(() => {
  let n = 0;
  if (parentEntry.value?._savePending) n += 1;
  for (const s of siblings.value) {
    if (s?._savePending) n += 1;
  }
  return n;
});

const ledgerStatusEmpty = computed(
  () => pendingSaveCount.value === 0 && !errorMessage.value,
);

const ledgerStatusClass = computed(() => {
  if (pendingSaveCount.value > 0) return 'is-pending';
  if (errorMessage.value && messageIsInfo.value) return 'is-info';
  if (errorMessage.value) return 'is-error';
  return 'is-empty';
});

const ledgerData = ref({});
const accountMatches = ref([]);
const splitMatches = ref([]);
const classMatches = ref([]);
const memoMatches = ref([]);
const nameMatches = ref([]);
const typeInput = ref();
const numInput = ref();
const amountInput = ref();
const accountInput = ref();
const splitInput = ref();
const classInput = ref();
const memoInput = ref();
const nameInput = ref();
const dateInput = ref();
const accountQuery = ref('');
const splitQuery = ref('');
const classQuery = ref(''); // If not already defined
const memoQuery = ref(''); // If not already defined
const nameQuery = ref(''); // If not already defined
const parentRowIndex = -1;
const errorMessage = ref('');
const messageIsInfo = ref(false);

/** Anchor input for teleported autocomplete (avoids overflow clipping). */
const dropdownAnchorEl = ref(null);
const dropdownFixedStyle = ref({});

const portalDropdownField = computed(() => activeEdit.value?.field || null);

const portalMatches = computed(() => {
  switch (portalDropdownField.value) {
    case 'Name':
      return nameMatches.value || [];
    case 'Memo':
      return memoMatches.value || [];
    case 'Account':
      return accountMatches.value || [];
    case 'Split':
      return splitMatches.value || [];
    case 'Class':
      return classMatches.value || [];
    default:
      return [];
  }
});

const portalDropdownOpen = computed(() => portalMatches.value.length > 0);

const portalDropdownExtraClass = computed(() => {
  const field = portalDropdownField.value;
  if (field === 'Account' || field === 'Split') return 'dropdown-account';
  if (field === 'Memo') return 'dropdown-memo';
  if (field === 'Class') return 'dropdown-class';
  return '';
});

function positionActiveDropdown() {
  const el = dropdownAnchorEl.value;
  if (!el || !portalMatches.value.length) {
    dropdownFixedStyle.value = {};
    return;
  }
  if (typeof el.getBoundingClientRect !== 'function') {
    dropdownFixedStyle.value = {};
    return;
  }

  const rect = el.getBoundingClientRect();
  const gap = 2;
  const preferredMax = 280;
  const spaceBelow = window.innerHeight - rect.bottom - 8;
  const spaceAbove = rect.top - 8;
  // Flip above when near the footer / bottom of the viewport
  const openUp = spaceBelow < 120 && spaceAbove > spaceBelow;
  const maxHeight = Math.max(
    80,
    Math.min(preferredMax, openUp ? spaceAbove : spaceBelow),
  );

  const field = portalDropdownField.value;
  let minWidth = rect.width;
  if (field === 'Account' || field === 'Split') minWidth = Math.max(rect.width, 225);
  else if (field === 'Class') minWidth = Math.max(rect.width, 220);
  else if (field === 'Memo') minWidth = Math.max(rect.width, 280);
  else if (field === 'Name') minWidth = Math.max(rect.width, 180);

  // Keep panel on-screen horizontally
  const left = Math.min(
    rect.left,
    Math.max(4, window.innerWidth - minWidth - 4),
  );

  if (openUp) {
    dropdownFixedStyle.value = {
      position: 'fixed',
      left: `${left}px`,
      width: `${minWidth}px`,
      bottom: `${window.innerHeight - rect.top + gap}px`,
      top: 'auto',
      maxHeight: `${maxHeight}px`,
      zIndex: 10050,
      overflowY: 'auto',
    };
  } else {
    dropdownFixedStyle.value = {
      position: 'fixed',
      left: `${left}px`,
      width: `${minWidth}px`,
      top: `${rect.bottom + gap}px`,
      bottom: 'auto',
      maxHeight: `${maxHeight}px`,
      zIndex: 10050,
      overflowY: 'auto',
    };
  }
}

function scheduleDropdownPosition(anchorEl = null) {
  if (anchorEl) dropdownAnchorEl.value = anchorEl;
  nextTick(() => {
    requestAnimationFrame(() => positionActiveDropdown());
  });
}

function onAutocompleteScroll() {
  if (portalMatches.value.length) positionActiveDropdown();
}

function emitPrintCheck() {
  eventBus.emit('ledgerEntryPrintCheck', currentId);
}

function editParentRow() {
  selectRow(parentEntry.value, parentRowIndex);
  showErrorMessage('Edit parent fields in place. Changes save when you leave a cell.', true);
}

function shortAccount(account) {
  if (!account) return '';
  const s = String(account);
  const colon = s.indexOf(':');
  return colon > 0 ? s.slice(0, colon).trim() : s;
}

function getActiveTarget() {
  const edit = activeEdit.value;
  if (!edit || edit.kind === 'single') return currentInput;
  if (edit.kind === 'parent') return parentEntry.value;
  if (edit.kind === 'sibling' && siblings.value[edit.index] != null) {
    return siblings.value[edit.index];
  }
  return currentInput;
}

function setActiveEdit(kind, index, field, event) {
  activeEdit.value = { kind, index, field };
  if (kind === 'sibling' && index != null) {
    selectedRow.value = { ...siblings.value[index], index };
  } else if (kind === 'parent') {
    selectedRow.value = { ...parentEntry.value, index: parentRowIndex };
  }
  if (event?.target) {
    dropdownAnchorEl.value = event.target;
    if (typeof event.target.select === 'function') {
      event.target.select();
    }
  }
  // Sync query strings for autocomplete
  const target = getActiveTarget();
  if (field === 'Name') nameQuery.value = target.Name || '';
  if (field === 'Memo') memoQuery.value = target.Memo || '';
  if (field === 'Account') accountQuery.value = target.Account || '';
  if (field === 'Split') splitQuery.value = target.Split || '';
  if (field === 'Class') classQuery.value = target.Class || '';
  scheduleDropdownPosition(event?.target || null);
}

function evaluateAmountExpression(raw) {
  try {
    const value = String(raw ?? '').trim();
    if (
      value.includes('+') ||
      value.includes('-') ||
      value.includes('*') ||
      value.includes('/') ||
      value.startsWith('=')
    ) {
      const expression = value.startsWith('=') ? value.substring(1) : value;
      const result = new Function(`return ${expression}`)();
      if (!isNaN(result)) return parseFloat(result).toFixed(2);
      return '0.00';
    }
    return isNaN(parseFloat(value)) ? '0.00' : parseFloat(value).toFixed(2);
  } catch {
    return '0.00';
  }
}

function onSpreadsheetNameInput(e, kind, index = null) {
  activeEdit.value = { kind, index, field: 'Name' };
  dropdownAnchorEl.value = e.target;
  nameQuery.value = e.target.value;
  const target = getActiveTarget();
  target.Name = e.target.value;
  searchNames();
}

function onSpreadsheetMemoInput(e, kind, index = null) {
  activeEdit.value = { kind, index, field: 'Memo' };
  dropdownAnchorEl.value = e.target;
  memoQuery.value = e.target.value;
  const target = getActiveTarget();
  target.Memo = e.target.value;
  searchMemos();
}

function onSpreadsheetAccountInput(e, kind, index = null) {
  activeEdit.value = { kind, index, field: 'Account' };
  dropdownAnchorEl.value = e.target;
  accountQuery.value = e.target.value;
  const target = getActiveTarget();
  target.Account = e.target.value;
  searchAccounts();
  maybeAutoCommitSingleAccountMatch(e.target);
}

function onSpreadsheetClassInput(e, kind, index = null) {
  activeEdit.value = { kind, index, field: 'Class' };
  dropdownAnchorEl.value = e.target;
  classQuery.value = e.target.value;
  const target = getActiveTarget();
  target.Class = e.target.value;
  searchClasses();
}

function flashSaved(msg) {
  const now = Date.now();
  // Avoid spamming the status line when tabbing quickly
  if (now - lastSaveMessageAt.value < 400) return;
  lastSaveMessageAt.value = now;
  showErrorMessage(msg || 'Saved.', true);
}

async function persistParentRow() {
  if (!isSplitMode.value && !parentEntry.value?.Id) return false;

  markParentSavePending();

  // Format date if needed
  if (parentEntry.value.Date) {
    const d = String(parentEntry.value.Date).trim();
    if (!/^\d{1,2}\/\d{1,2}\/\d{2}$/.test(d)) {
      const formatted = formatDate(parentEntry.value.Date);
      if (formatted) parentEntry.value.Date = formatted;
    }
  }

  const mysqlFormattedDate = convertToMySQLFormat(
    String(parentEntry.value.Date || '').trim(),
  );
  if (!mysqlFormattedDate) {
    showErrorMessage('Date is required to save the parent row.');
    clearParentSavePending({ error: true });
    return false;
  }

  parentEntry.value.Amount = -formatAmount(totalSiblingAmount.value);
  parentEntry.value.Split = '-SPLIT-';

  const parentPayload = {
    Date: mysqlFormattedDate,
    Type: parentEntry.value.Type || '',
    Num: parentEntry.value.Num || '',
    Amount: parentEntry.value.Amount,
    Name: parentEntry.value.Name || '',
    Memo: parentEntry.value.Memo || '',
    Account: parentEntry.value.Account || props.account || '',
    Split: '-SPLIT-',
    Class: parentEntry.value.Class || '',
    IsSplitParent: true,
    LinkedSplitId: null,
  };

  // Allow nested call from persistSiblingRow without releasing the lock early
  const acquiredLock = !isSaving.value;
  if (acquiredLock) isSaving.value = true;
  let ok = false;
  try {
    if (parentEntry.value.Id) {
      await axios.post(
        '/api/update-ledger-entry',
        {
          Id: parentEntry.value.Id,
          ...parentPayload,
        },
        { timeout: LEDGER_AXIOS_TIMEOUT_MS },
      );
      eventBus.emit('ledgerEntrySaved', parentEntry.value.Id);
    } else {
      const response = await axios.put(
        '/api/add-ledger-entry',
        parentPayload,
        { timeout: LEDGER_AXIOS_TIMEOUT_MS },
      );
      if (response.status === 201) {
        parentEntry.value.Id = response.data.id;
        eventBus.emit('ledgerEntrySaved', parentEntry.value.Id);
      } else {
        showErrorMessage('Failed to create parent ledger entry.');
        return false;
      }
    }
    parentAmountNeedsSync = false;
    ok = true;
    // Nested parent sync (from child save) stays quiet — no footer jitter.
    if (acquiredLock) {
      flashSaved(`Parent #${parentEntry.value.Id} saved.`);
    }
    return true;
  } catch (error) {
    console.error('Error saving parent row:', error);
    const timedOut =
      error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '');
    showErrorMessage(
      timedOut
        ? 'Timed out saving parent row. Check network/DB and try Save again.'
        : 'Failed to save parent row.',
    );
    return false;
  } finally {
    clearParentSavePending({ error: !ok });
    if (acquiredLock) isSaving.value = false;
  }
}

/**
 * @param {number} index
 * @param {{ syncParent?: boolean, announce?: boolean }} [options]
 *   syncParent: also persist parent so Amount = -sum(children).
 *   announce: show green “Split line saved.” footer (Class leave / explicit Save only).
 */
async function persistSiblingRow(index, options = {}) {
  const { syncParent = true, announce = false } = options;
  const sibling = siblings.value[index];
  if (!sibling) return false;

  markSiblingSavePending(index);

  // Ensure parent exists first (needed for LinkedSplitId)
  if (!parentEntry.value?.Id) {
    const ok = await persistParentRow();
    if (!ok) {
      clearSiblingSavePending(index, { error: true });
      return false;
    }
  }

  // Snapshot fields now — another cell may change shared query refs while we await
  const amount = evaluateAmountExpression(sibling.Amount);
  sibling.Amount = amount;
  if (!sibling.Date) sibling.Date = parentEntry.value.Date;
  const account = sibling.Account || '';
  const memo = sibling.Memo || '';
  const classVal = sibling.Class || '';
  const siblingId = sibling.Id;

  // Normalize parent date to mm/dd/yy for MySQL conversion
  let parentDateStr = parentEntry.value.Date;
  if (
    parentDateStr &&
    !/^\d{1,2}\/\d{1,2}\/\d{2}$/.test(String(parentDateStr))
  ) {
    parentDateStr = formatDate(parentDateStr);
    parentEntry.value.Date = parentDateStr;
  }
  const dateForSql = convertToMySQLFormat(String(parentDateStr || '').trim());
  if (!dateForSql) {
    showErrorMessage('Parent date is required before saving split lines.');
    clearSiblingSavePending(index, { error: true });
    return false;
  }

  const siblingPayload = {
    Date: dateForSql,
    Type: parentEntry.value.Type || '',
    Num: parentEntry.value.Num || '',
    Amount: amount,
    Name: parentEntry.value.Name || '',
    Memo: memo,
    Account: account,
    Split: parentEntry.value.Account || '',
    Class: classVal,
    IsSplitParent: false,
    LinkedSplitId: parentEntry.value.Id,
  };

  isSaving.value = true;
  let ok = false;
  try {
    if (siblingId && siblingId !== -1) {
      await axios.post(
        '/api/update-ledger-entry',
        {
          Id: siblingId,
          ...siblingPayload,
        },
        { timeout: LEDGER_AXIOS_TIMEOUT_MS },
      );
      // Only merge fields we own — avoid clobbering concurrent UI edits on Date display
      sibling.Amount = amount;
      sibling.Memo = memo;
      sibling.Account = account;
      sibling.Class = classVal;
      sibling.Split = siblingPayload.Split;
      eventBus.emit('ledgerEntrySaved', siblingId);
    } else {
      const response = await axios.put(
        '/api/add-ledger-entry',
        siblingPayload,
        { timeout: LEDGER_AXIOS_TIMEOUT_MS },
      );
      if (response.status === 201 && response.data?.id != null) {
        const savedId = response.data.id;
        sibling.Id = savedId;
        sibling.Amount = amount;
        sibling.Memo = memo;
        sibling.Account = account;
        sibling.Class = classVal;
        sibling.Split = siblingPayload.Split;
        eventBus.emit('ledgerEntrySaved', savedId);
      } else {
        showErrorMessage('Failed to create split line (no id returned).');
        return false;
      }
    }

    // Parent total depends on children; amount edits (and new lines) need sync.
    parentAmountNeedsSync = true;
    if (syncParent) {
      await persistParentRow();
    }
    sibling._dirty = false;
    if (announce) {
      flashSaved('Split line saved.');
    }
    ok = true;
    return true;
  } catch (error) {
    console.error('Error saving sibling row:', error);
    const timedOut =
      error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '');
    showErrorMessage(
      timedOut
        ? 'Timed out saving split line. Check network/DB and try Save again.'
        : 'Failed to save split line.',
    );
    return false;
  } finally {
    clearSiblingSavePending(index, { error: !ok });
    isSaving.value = false;
  }
}

function onParentBlur(field) {
  scheduleBlurSave(async () => {
    if (field === 'Date') {
      const d = String(parentEntry.value.Date || '').trim();
      const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/;
      if (dateRegex.test(d)) {
        const [, month, day, year] = d.match(dateRegex);
        parentEntry.value.Date = `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
      } else if (d) {
        const formatted = formatDate(d);
        if (formatted) parentEntry.value.Date = formatted;
      }
    }
    if (field === 'Name' && nameQuery.value) {
      parentEntry.value.Name = nameQuery.value;
    }
    if (field === 'Memo') {
      parentEntry.value.Memo = memoQuery.value || parentEntry.value.Memo;
    }
    if (field === 'Account' && accountQuery.value) {
      parentEntry.value.Account =
        accountMatches.value[0]?.value || accountQuery.value;
    }
    clearSearch();
    await persistParentRow();
  });
}

function onParentNumBlur() {
  scheduleBlurSave(async () => {
    if (parentEntry.value.Num === '>>') {
      try {
        const response = await axios.get('/api/get-max-num');
        parentEntry.value.Num = response.data + 1;
      } catch (error) {
        console.error('Error fetching next number:', error);
      }
    }
    await persistParentRow();
  });
}

/**
 * Amount / Memo / Account: commit values locally only.
 * Network save + status banner only when leaving Class (Tab or blur) or Save.
 */
function onSiblingAmountBlur(index) {
  const sibling = siblings.value[index];
  if (!sibling) return;
  sibling.Amount = evaluateAmountExpression(sibling.Amount);
  sibling._dirty = true;
  parentAmountNeedsSync = true;
  clearSearch();
}

function onSiblingBlur(index, field) {
  const sibling = siblings.value[index];
  if (!sibling) return;

  // Commit shared query strings to THIS sibling now, before focus moves and
  // accountQuery/memoQuery/classQuery are reused for another row.
  if (field === 'Memo') {
    sibling.Memo = memoQuery.value || sibling.Memo || '';
    sibling._dirty = true;
    clearSearch();
    return;
  }
  if (field === 'Account') {
    sibling.Account =
      accountMatches.value[0]?.value || accountQuery.value || sibling.Account;
    sibling._dirty = true;
    clearSearch();
    return;
  }
  if (field === 'Class') {
    sibling.Class =
      classMatches.value[0]?.value || classQuery.value || sibling.Class;
    sibling._dirty = true;
    clearSearch();
    // Tab-from-Class already enqueued a save; skip duplicate blur save.
    if (sibling._savePending) return;
    // Click-away from Class: persist the completed line.
    markSiblingSavePending(index);
    scheduleBlurSave(() =>
      persistSiblingRow(index, { syncParent: true, announce: true }),
    );
    return;
  }
  sibling._dirty = true;
  clearSearch();
}

function onSingleFieldBlur(field) {
  scheduleBlurSave(async () => {
    if (field === 'Date') validateAndFormatDate();
    if (field === 'Name') currentInput.Name = nameQuery.value;
    if (field === 'Memo') currentInput.Memo = memoQuery.value;
    if (field === 'Account') {
      currentInput.Account =
        accountMatches.value[0]?.value || accountQuery.value;
    }
    if (field === 'Split') {
      currentInput.Split = splitMatches.value[0]?.value || splitQuery.value;
    }
    if (field === 'Class') {
      currentInput.Class = classMatches.value[0]?.value || classQuery.value;
    }
    clearSearch();
    if (currentInput.Id || parentEntry.value?.Id) {
      await saveEntryQuiet();
    }
  });
}

function onSingleNumBlur() {
  handleNumBlur();
  onSingleFieldBlur('Num');
}

function onSingleAmountBlur() {
  handleAmountBlur();
  onSingleFieldBlur('Amount');
}

function openDeleteConfirmation() {
  modalContent.title = 'Delete Confirmation';
  modalContent.message = 'Are you sure you want to delete this ledger entry?';
  modalContent.actionType = 'delete';
  showModal.value = true;
}

function handleModalConfirm(actionType) {
  if (actionType === 'delete') {
    softDeleteLedgerEntry();
  }
  // Close the modal after handling
  showModal.value = false;
}

async function hideModal() {
  if (isClosing.value) return;
  isClosing.value = true;

  // Remove global event listeners when modal is closing
  document.removeEventListener('keydown', preventGlobalKeyEvents, true);
  document.removeEventListener('keyup', preventGlobalKeyEvents, true);
  document.removeEventListener('keypress', preventGlobalKeyEvents, true);

  // Finish focused field + dirty children + parent total if needed.
  // Entire path is time-bounded (flushSavesOnClose + awaitSaveChain) so a
  // hung axios call cannot leave "Saving changes before closing…" forever.
  try {
    await nextTick(); // paint spinner before we block on network
    commitActiveSiblingFromQueries();
    await flushSavesOnClose();
  } catch (e) {
    console.error('Error flushing saves on close:', e);
    isSaving.value = false;
    saveChain = Promise.resolve();
  }

  // Just close the modal without performing any action
  showModal.value = false;
  siblings.value = [];
  ledgerData.value = {};
  // Fix: currentInput is a reactive object, not a ref
  // Instead of currentInput.value = {}, use Object.assign
  Object.assign(currentInput, {});
  isClosing.value = false;
  isSaving.value = false;
  console.log('closing');

  // Only emit closeLedgerEntry event
  eventBus.emit('closeLedgerEntry');
}

function showErrorMessage(theMessage, infoOnly = false) {
  errorMessage.value = theMessage;
  messageIsInfo.value = infoOnly;
}

function showErrorModal(message) {
  modalContent.title = 'Error';
  modalContent.message = message;
  modalContent.actionType = ''; // No action required on confirmation
  showModal.value = true;
}

function softDeleteLedgerEntry() {
  if (!currentId && !selectedRow.value?.Id && !parentEntry.value?.Id) return;

  // Prefer selected sibling/parent; fall back to dialog id
  const idToDelete =
    selectedRow.value?.Id && selectedRow.value.Id !== -1
      ? selectedRow.value.Id
      : parentEntry.value?.Id || currentId;

  axios
    .post('/api/del-ledger-entry', { id: idToDelete })
    .then(async (response) => {
      if (response.status === 200) {
        const isParent =
          idToDelete === parentEntry.value?.Id || idToDelete === props.id;
        if (isParent) {
          hideModal();
          return;
        }
        siblings.value = siblings.value.filter(
          (sibling) => sibling.Id !== idToDelete,
        );
        selectedRow.value = null;
        if (isSplitMode.value) {
          await persistParentRow();
        }
        showErrorMessage('Split line deleted.', true);
      }
    })
    .catch((error) => {
      console.error('Failed to delete ledger entry:', error);
      showErrorModal('Failed to delete ledger entry. Please try again.');
    });
}

// Functions to manage IndexedDB
function getFromIndexedDB(db, storeName) {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get('data');

    request.onsuccess = () => resolve(request.result);
    request.onerror = (event) => reject(event.target.error);
  });
}

function saveToIndexedDB(db, storeName, data) {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(data, 'data');

    request.onsuccess = () => resolve();
    request.onerror = (event) => reject(event.target.error);
  });
}

// Load initial data into the reactive state
async function loadInitialData() {
  try {
    const dbPromise = indexedDB.open('AppDatabase', 1);

    dbPromise.onupgradeneeded = function (event) {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('classes')) {
        db.createObjectStore('classes');
      }
      if (!db.objectStoreNames.contains('chartOfAccounts')) {
        db.createObjectStore('chartOfAccounts');
      }
      if (!db.objectStoreNames.contains('memos')) {
        db.createObjectStore('memos');
      }
      if (!db.objectStoreNames.contains('names')) {
        db.createObjectStore('names');
      }
    };

    const db = await new Promise((resolve, reject) => {
      dbPromise.onsuccess = (event) => resolve(event.target.result);
      dbPromise.onerror = (event) => reject(event.target.error);
    });

    // Load classes data
    let classesData = await getFromIndexedDB(db, 'classes');
    if (!classesData) {
      const classResponse = await axios.get('/api/get-classes');
      classesData = classResponse.data;
      await saveToIndexedDB(db, 'classes', classesData);
    }
    classes.value = classesData;

    // Load accounts data
    let accountsData = await getFromIndexedDB(db, 'chartOfAccounts');
    if (!accountsData) {
      const accountsResponse = await axios.get('/api/get-chart-of-accounts');
      accountsData = accountsResponse.data;
      await saveToIndexedDB(db, 'chartOfAccounts', accountsData);
    }
    chartOfAccounts.value = accountsData;

    // Load memos data
    let memosData = await getFromIndexedDB(db, 'memos');
    if (!memosData) {
      const memosResponse = await axios.get('/api/get-memos');
      memosData = memosResponse.data;
      await saveToIndexedDB(db, 'memos', memosData);
    }
    memos.value = memosData;

    // Load names data
    let namesData = await getFromIndexedDB(db, 'names');
    if (!namesData) {
      const namesResponse = await axios.get('/api/get-names');
      namesData = namesResponse.data;
      await saveToIndexedDB(db, 'names', namesData);
    }
    names.value = namesData;

    if (currentId) {
      await fetchLedgerEntry(currentId);
    }
  } catch (error) {
    console.error('Error loading initial data:', error);
  }
}

// Refresh autocomplete data from API and update IndexedDB cache
async function refreshAutocompleteData() {
  try {
    showErrorMessage('Refreshing autocomplete data...', true);

    const dbPromise = indexedDB.open('AppDatabase', 1);
    const db = await new Promise((resolve, reject) => {
      dbPromise.onsuccess = (event) => resolve(event.target.result);
      dbPromise.onerror = (event) => reject(event.target.error);
    });

    // Fetch and update classes data
    const classResponse = await axios.get('/api/get-classes');
    await saveToIndexedDB(db, 'classes', classResponse.data);
    classes.value = classResponse.data;

    // Fetch and update accounts data
    const accountsResponse = await axios.get('/api/get-chart-of-accounts');
    await saveToIndexedDB(db, 'chartOfAccounts', accountsResponse.data);
    chartOfAccounts.value = accountsResponse.data;

    // Fetch and update memos data
    const memosResponse = await axios.get('/api/get-memos');
    await saveToIndexedDB(db, 'memos', memosResponse.data);
    memos.value = memosResponse.data;

    // Fetch and update names data
    const namesResponse = await axios.get('/api/get-names');
    await saveToIndexedDB(db, 'names', namesResponse.data);
    names.value = namesResponse.data;

    showErrorMessage('Autocomplete data refreshed successfully!', true);

    // Clear the message after 2 seconds
    setTimeout(() => {
      showErrorMessage('', false);
    }, 2000);
  } catch (error) {
    console.error('Error refreshing autocomplete data:', error);
    showErrorMessage('Failed to refresh autocomplete data. Please try again.');
  }
}

// Fetch ledger entry and update the reactive state
async function fetchLedgerEntry(id) {
  try {
    isSplitMode.value = false; // Reset split mode
    const getLedgerResponse = await axios.get(`/api/get-ledger-entry/${id}`);
    ledgerData.value = getLedgerResponse.data;
    // Check for parentEntry and optionally siblings array
    if (ledgerData.value && ledgerData.value.parentEntry) {
      ledgerData.value.parentEntry.Date = formatDate(
        ledgerData.value.parentEntry.Date,
      );
      Object.assign(parentEntry.value, ledgerData.value.parentEntry);
    }
    if (
      Array.isArray(ledgerData.value.siblings) &&
      ledgerData.value.siblings.length > 0
    ) {
      isSplitMode.value = true;
      initializeEntry(ledgerData.value);
    } else {
      Object.assign(currentInput, ledgerData.value.parentEntry);
      nameQuery.value = currentInput.Name;
      memoQuery.value = currentInput.Memo;
      accountQuery.value = currentInput.Account;
      splitQuery.value = currentInput.Split;
      classQuery.value = currentInput.Class;
      // console.log('currentInput', JSON.stringify(currentInput));
    }
  } catch (error) {
    console.error('Error fetching ledger entry:', error);
  }
}

function initializeEntry(data) {
  if (data.siblings) {
    siblings.value = (data.siblings || []).map((s) =>
      assignSiblingRowKey({
        ...s,
        Date: formatDate(s.Date) || s.Date,
        Amount:
          s.Amount != null && s.Amount !== ''
            ? parseFloat(s.Amount).toFixed(2)
            : '0.00',
      }),
    );
    isSplitMode.value = true;
    if (parentEntry.value.Date) {
      parentEntry.value.Date =
        formatDate(parentEntry.value.Date) || parentEntry.value.Date;
    }
  }
}

/** Amount for a new ledger row from a bank txn: prefer non-zero Credit, else Debit. */
function amountFromBankTransaction(data) {
  if (!data) return 0;
  const credit = parseFloat(data.Credit);
  const debit = parseFloat(data.Debit);
  if (!isNaN(credit) && credit !== 0) return data.Credit;
  if (!isNaN(debit) && debit !== 0) return data.Debit;
  return data.Credit || data.Debit || 0;
}

// Lifecycle hook for initial data loading
onMounted(async () => {
  await loadInitialData();
  showErrorMessage(
    isSplitMode.value
      ? 'Edit split rows in place. Cells save on leave; + Split saves the current line first. Save/X only write modified lines + parent total. Ctrl-Enter saves and closes.'
      : 'Edit the fields between the blue lines. Changes save when you leave a field (or press ENTER). Ctrl-Enter saves and closes.',
    true,
  );

  // Ensure the modal captures focus to prevent event propagation
  const modalElement = document.querySelector('.ledger-entry-modal');
  if (modalElement) {
    modalElement.focus();
    modalElement.setAttribute('tabindex', '-1');
  }

  // Add global event listeners to prevent propagation when modal is active
  document.addEventListener('keydown', preventGlobalKeyEvents, true);
  document.addEventListener('keyup', preventGlobalKeyEvents, true);
  document.addEventListener('keypress', preventGlobalKeyEvents, true);
  window.addEventListener('resize', onAutocompleteScroll);

  if (props.isNew) {
    currentInput.Id = 0;
    currentInput.Account = '';
    accountQuery.value = currentInput.Account;
    currentInput.Split = props.account || '';
    splitQuery.value = currentInput.Split;
    parentEntry.value.Split = currentInput.Split;
    // Pre-fill fields from bank transaction data
    if (props.data) {
      currentInput.Date = props.data.BankRefDateTime || formatDate(new Date());
      currentInput.Memo = memoQuery.value = props.data.BankRefDesc || '';
      currentInput.Num = props.data.BankRefCheckNum || '';
      currentInput.Type = props.data.BankType || '';
      currentInput.LinkedTransactionId = props.data.Id;
      // Prefer Credit when non-zero (string "0"/"0.00" is truthy, so avoid Debit || Credit)
      currentInput.Amount = amountFromBankTransaction(props.data);
    } else {
      currentInput.Date = formatDate(new Date());
    }
    nextTick(() => {
      if (numInput.value) {
        typeInput.value.focus(); // Set focus to the input element
      }
    });
  } else if (props.data && Object.keys(props.data).length > 0) {
    currentInput.Date = props.data.BankRefDateTime;
    currentInput.Memo = memoQuery.value = props.data.BankRefDesc;
    currentInput.Num = props.data.BankRefCheckNum;
    currentInput.Name = nameQuery.value = '';
    currentInput.Type = props.data.BankType;
    currentInput.LinkedTransactionId = props.data.Id;
    currentInput.Account = accountQuery.value = props.account;
    currentInput.Split = splitQuery.value = '';
    currentInput.Class = classQuery.value = '';
    currentInput.Amount = amountFromBankTransaction(props.data);
    nextTick(() => {
      if (nameInput.value) {
        nameInput.value.focus(); // Set focus to the input element
      }
    });
  } else {
    currentInput.Date = parentEntry.value.Date;
  }
});

// Cleanup when component is unmounted
onBeforeUnmount(() => {
  // Remove global event listeners to prevent memory leaks
  document.removeEventListener('keydown', preventGlobalKeyEvents, true);
  document.removeEventListener('keyup', preventGlobalKeyEvents, true);
  document.removeEventListener('keypress', preventGlobalKeyEvents, true);
  window.removeEventListener('resize', onAutocompleteScroll);
  // Best-effort: kick pending blur saves into the queue (async may not finish)
  flushPendingBlurSave();
});

function validateAndFormatDate() {
  const date = currentInput.Date.trim();
  const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/; // mm/dd/yy

  if (dateRegex.test(date)) {
    const [, month, day, year] = date.match(dateRegex);
    const formattedDate = `${month.padStart(2, '0')}/${day.padStart(2, '0')}/${year}`;
    currentInput.Date = formattedDate;
  } else if (date) {
    showErrorModal('Invalid date format. Please use mm/dd/yy.');
    currentInput.Date = '';
  }
}

function swapAccountAndSplit() {
  // Swap the values of Account and Split fields
  const tempAccount = accountQuery.value;
  const tempSplit = splitQuery.value;

  accountQuery.value = tempSplit;
  splitQuery.value = tempAccount;

  currentInput.Account = tempSplit;
  currentInput.Split = tempAccount;

  // Trigger search for the new values to populate dropdowns
  searchAccounts();
  searchSplits();
}

async function addSibling() {
  const wasSplitModeAlready = isSplitMode.value;
  isSplitMode.value = true;

  // Convert single entry → split: parent shell + first child from current line
  if (!wasSplitModeAlready || siblings.value.length === 0) {
    if (!wasSplitModeAlready) {
      Object.assign(parentEntry.value, { ...currentInput });
      parentEntry.value.Account = props.account || currentInput.Split || '';
      parentEntry.value.Split = '-SPLIT-';
      parentEntry.value.IsSplitParent = true;
    }

    if (currentInput.Amount && parseFloat(currentInput.Amount) !== 0) {
      const first = assignSiblingRowKey({
        Id: -1,
        Date: parentEntry.value.Date,
        Amount: parseFloat(currentInput.Amount).toFixed(2),
        Memo: currentInput.Memo || memoQuery.value || '',
        Account: currentInput.Account || accountQuery.value || '',
        Class: currentInput.Class || classQuery.value || parentEntry.value.Class || '',
        Name: '',
        Type: parentEntry.value.Type || '',
        Num: parentEntry.value.Num || '',
        Split: parentEntry.value.Account || '',
        IsSplitParent: false,
        LinkedSplitId: parentEntry.value.Id || null,
        _dirty: true,
      });
      siblings.value.push(first);
      await persistParentRow();
      await persistSiblingRow(0);
    } else {
      await persistParentRow();
    }
  } else {
    // Already in split mode: commit + save the row being left before a new line.
    // (Blur from the click also schedules a save; flush it — do not cancel.)
    commitActiveSiblingFromQueries();
    await flushAllPendingSaves();
    await enqueueSave(() => persistDirtySiblings({ syncParentIfNeeded: true }));
    await awaitSaveChain(CLOSE_FLUSH_MS);
  }

  // Append a blank child row for spreadsheet editing
  await appendBlankSiblingAndFocusAmount();
  showErrorMessage(
    'New split line ready. Each line saves when you Tab out of Class (or click Save).',
    true,
  );
}

/** Stable v-for key so assigning a real Id after first save does not remount the row (and steal focus). */
let siblingRowKeySeq = 0;
function assignSiblingRowKey(sibling) {
  if (!sibling._rowKey) {
    sibling._rowKey =
      sibling.Id && sibling.Id !== -1
        ? `id-${sibling.Id}`
        : `tmp-${++siblingRowKeySeq}`;
  }
  return sibling;
}

function createBlankSibling() {
  const prev = siblings.value[siblings.value.length - 1];
  // New line: blank Memo/Class; keep Account from previous for convenience
  return assignSiblingRowKey({
    Id: -1,
    Date: parentEntry.value.Date,
    Amount: '0.00',
    Memo: '',
    Account: prev?.Account || siblings.value[0]?.Account || '',
    Class: '',
    Name: '',
    Type: parentEntry.value.Type || '',
    Num: parentEntry.value.Num || '',
    Split: parentEntry.value.Account || '',
    IsSplitParent: false,
    LinkedSplitId: parentEntry.value.Id || null,
    _dirty: false,
  });
}

function focusSiblingAmount(index) {
  const rows = document.querySelectorAll('.sibling-row.spreadsheet-row');
  const amountEl = rows[index]?.querySelector('.amount-input');
  if (amountEl && typeof amountEl.focus === 'function') {
    amountEl.focus();
    if (typeof amountEl.select === 'function') amountEl.select();
  }
}

async function appendBlankSiblingAndFocusAmount() {
  const newIndex = siblings.value.length;
  siblings.value.push(createBlankSibling());
  // Do not leave prior row's Class query active on the new blank line
  classQuery.value = '';
  memoQuery.value = '';
  accountQuery.value = '';
  selectedRow.value = { ...siblings.value[newIndex], index: newIndex };
  activeEdit.value = { kind: 'sibling', index: newIndex, field: 'Amount' };
  await nextTick();
  focusSiblingAmount(newIndex);
  // Prior row was already flushed/saved in addSibling — clear any redundant
  // blur job from the focus move onto this blank amount field.
  cancelBlurSave();
}

function resetInput() {
  Object.assign(currentInput, {
    Id: null,
    Date: formatDate(new Date()),
    Num: '0.00',
    Amount: '',
    Name: '',
    Memo: '',
    Split: '',
    Account: '',
    Class: '',
  });
  memoQuery.value = '';
  accountQuery.value = '';
  classQuery.value = '';
  splitQuery.value = '';
  nameQuery.value = '';
}

function handleNumBlur() {
  if (currentInput.Num === '>>') {
    fetchNextNum();
  }
}

function handleAmountBlur() {
  try {
    const value = currentInput.Amount.toString().trim();

    // Check if the input appears to be an expression
    if (
      value.includes('+') ||
      value.includes('-') ||
      value.includes('*') ||
      value.includes('/') ||
      value.startsWith('=')
    ) {
      // Remove the = prefix if it exists
      const expression = value.startsWith('=') ? value.substring(1) : value;

      // Use Function constructor to safely evaluate the expression
      const result = new Function(`return ${expression}`)();

      // Check if the result is a valid number
      if (!isNaN(result)) {
        currentInput.Amount = parseFloat(result).toFixed(2);
      } else {
        currentInput.Amount = '0.00';
      }
    } else {
      // Original behavior for plain numbers
      currentInput.Amount = isNaN(parseFloat(value))
        ? '0.00'
        : parseFloat(value).toFixed(2);
    }
  } catch (error) {
    console.error('Expression evaluation error:', error);
    currentInput.Amount = '0.00';
  }
}

async function fetchNextNum() {
  try {
    const response = await axios.get('/api/get-max-num');
    currentInput.Num = response.data + 1;
  } catch (error) {
    console.error('Error fetching next number:', error);
  }
}

function formatAmount(val) {
  return parseFloat(val).toFixed(2);
}

function highlightMatch(value, query) {
  const regex = new RegExp(`(${query})`, 'gi');
  return value.replace(regex, '<span class="highlight-term">$1</span>');
}

function handleAccountInput(e) {
  // Update typedQuery with whatever the user has typed
  accountQuery.value = e.target.value;
  dropdownAnchorEl.value = e.target;
  searchAccounts();
  maybeAutoCommitSingleAccountMatch(e.target);
}

/**
 * When paste/type leaves exactly one COA match (typical when pasting a full
 * Account value from another row), accept it and leave the field so the user
 * does not have to click the single suggestion.
 */
function maybeAutoCommitSingleAccountMatch(inputEl) {
  if (accountMatches.value.length !== 1) return;

  const match = accountMatches.value[0].value;
  accountQuery.value = match;
  getActiveTarget().Account = match;
  accountMatches.value = [];
  recordFieldUse('Account', match);

  nextTick(() => {
    if (inputEl && typeof inputEl.blur === 'function') {
      inputEl.blur();
    }
  });
}

function handleSplitInput(e) {
  // Update typedQuery with whatever the user has typed
  splitQuery.value = e.target.value;
  dropdownAnchorEl.value = e.target;
  searchSplits();
}

/** Shift+Tab on first field: stay put (do not wrap to last control). */
function preventShiftTabWrapFromFirst(e) {
  if (e.key === 'Tab' && e.shiftKey) {
    e.preventDefault();
    e.stopPropagation();
  }
}

/** Single-entry field order for Tab / Shift+Tab (no wrap past first/last). */
function getSingleModeFieldEls() {
  return [
    dateInput.value,
    typeInput.value,
    numInput.value,
    amountInput.value,
    nameInput.value,
    memoInput.value,
    accountInput.value,
    splitInput.value,
    classInput.value,
  ].filter((el) => el && typeof el.focus === 'function');
}

function commitAutocompleteOnTab(field) {
  if (field === 'Name') {
    // Name does not auto-pick matches[0] on Tab — only record via click/↓
    getActiveTarget().Name = nameQuery.value;
    nameMatches.value = [];
  } else if (field === 'Memo') {
    getActiveTarget().Memo = memoQuery.value;
    memoMatches.value = [];
  } else if (field === 'Account') {
    if (accountMatches.value.length > 0) {
      const match = accountMatches.value[0].value;
      accountQuery.value = match;
      getActiveTarget().Account = match;
      recordFieldUse('Account', match);
    } else {
      getActiveTarget().Account =
        accountQuery.value || getActiveTarget().Account;
    }
    accountMatches.value = [];
  } else if (field === 'Split') {
    if (splitMatches.value.length > 0) {
      const match = splitMatches.value[0].value;
      currentInput.Split = match;
      splitQuery.value = match;
      recordFieldUse('Split', match);
    }
    splitMatches.value = [];
  } else if (field === 'Class') {
    if (classMatches.value.length > 0) {
      const match = classMatches.value[0].value;
      classQuery.value = match;
      getActiveTarget().Class = match;
      recordFieldUse('Class', match);
    } else {
      getActiveTarget().Class = classQuery.value || getActiveTarget().Class;
    }
    classMatches.value = [];
  }
}

/**
 * Tab → next field; Shift+Tab → previous. Does not wrap first↔last.
 * On last field (Class), Tab blurs so blur-save runs.
 */
function handleSingleModeTabKey(e, field) {
  if (e.key !== 'Tab') return;

  const fields = getSingleModeFieldEls();
  const current = e.target;
  let idx = fields.indexOf(current);
  if (idx < 0) {
    // Fallback: map field name to known ref
    const byField = {
      Date: dateInput.value,
      Type: typeInput.value,
      Num: numInput.value,
      Amount: amountInput.value,
      Name: nameInput.value,
      Memo: memoInput.value,
      Account: accountInput.value,
      Split: splitInput.value,
      Class: classInput.value,
    };
    idx = fields.indexOf(byField[field]);
  }
  if (idx < 0) return;

  e.preventDefault();
  e.stopPropagation();
  commitAutocompleteOnTab(field);

  nextTick(() => {
    if (e.shiftKey) {
      if (idx > 0) fields[idx - 1].focus();
      return;
    }
    if (idx < fields.length - 1) {
      fields[idx + 1].focus();
      return;
    }
    // Last field: blur to trigger save, do not wrap to first
    if (current && typeof current.blur === 'function') {
      current.blur();
    }
  });
}

function searchAccounts() {
  const query = accountQuery.value.trim().toLowerCase();
  if (!query) {
    accountMatches.value = [];
    return;
  }
  // Only build the suggestion list — do NOT overwrite the typed value on
  // every keystroke (that made split-row Account cells jump to unrelated
  // accounts, e.g. "E" → "10000 : General Checking"). Single-match paste is
  // committed separately via maybeAutoCommitSingleAccountMatch.
  const filtered = chartOfAccounts.value
    .filter((item) => {
      const combined =
        `${item.accountNumber} : ${item.accountName}`.toLowerCase();
      return combined.includes(query);
    })
    .map((item) => ({
      value: `${item.accountNumber} : ${item.accountName}`,
      highlighted: highlightMatch(
        `${item.accountNumber} : ${item.accountName}`,
        query,
      ),
    }));
  accountMatches.value = rankMatches(MRU_CATEGORIES.COA, filtered, {
    getValue: (m) => m.value,
  });

  // Keep the active cell text as what the user typed (until single-match commit)
  getActiveTarget().Account = accountQuery.value;
  scheduleDropdownPosition();
}

function searchSplits() {
  const query = splitQuery.value.trim().toLowerCase();
  if (!query) {
    splitMatches.value = [];
    return;
  }
  const filtered = chartOfAccounts.value
    .filter((item) => {
      const combined =
        `${item.accountNumber} : ${item.accountName}`.toLowerCase();
      return combined.includes(query);
    })
    .map((item) => ({
      value: `${item.accountNumber} : ${item.accountName}`,
      highlighted: highlightMatch(
        `${item.accountNumber} : ${item.accountName}`,
        query,
      ),
    }));
  splitMatches.value = rankMatches(MRU_CATEGORIES.COA, filtered, {
    getValue: (m) => m.value,
  });
  scheduleDropdownPosition();

  getActiveTarget().Split = splitQuery.value;
}

function handleMemoInput(e) {
  // Update typedQuery with whatever the user has typed
  memoQuery.value = e.target.value;
  dropdownAnchorEl.value = e.target;
  searchMemos();
}

function searchMemos() {
  const query = memoQuery.value.trim().toLowerCase();
  if (!query) {
    memoMatches.value = [];
    return;
  }
  const filtered = memos.value
    .filter((item) => item?.toLowerCase().includes(query))
    .map((item) => ({
      value: item,
      highlighted: highlightMatch(item, query),
    }));
  memoMatches.value = rankMatches(MRU_CATEGORIES.MEMO, filtered, {
    getValue: (m) => m.value,
  });

  getActiveTarget().Memo = memoQuery.value;
  scheduleDropdownPosition();
}

function handleNameInput(e) {
  nameQuery.value = e.target.value;
  dropdownAnchorEl.value = e.target;
  searchNames();
}

function handleAutocompleteArrowDown(
  e,
  matches,
  applyMatch,
  nextInputRef,
  fieldForMru,
) {
  if (!matches.value.length) return;

  e.preventDefault();
  e.stopPropagation();

  const value = matches.value[0].value;
  applyMatch(value);
  if (fieldForMru) recordFieldUse(fieldForMru, value);
  matches.value = [];

  nextTick(() => {
    if (nextInputRef?.value?.focus) {
      nextInputRef.value.focus();
    }
  });
}

function handleNameArrowDown(e) {
  handleAutocompleteArrowDown(
    e,
    nameMatches,
    (value) => {
      nameQuery.value = value;
      getActiveTarget().Name = value;
    },
    memoInput,
    'Name',
  );
}

function handleMemoArrowDown(e) {
  handleAutocompleteArrowDown(
    e,
    memoMatches,
    (value) => {
      memoQuery.value = value;
      getActiveTarget().Memo = value;
    },
    accountInput,
    'Memo',
  );
}

function handleAccountArrowDown(e) {
  handleAutocompleteArrowDown(
    e,
    accountMatches,
    (value) => {
      accountQuery.value = value;
      getActiveTarget().Account = value;
    },
    isSplitMode.value ? classInput : splitInput,
    'Account',
  );
}

function handleSplitArrowDown(e) {
  handleAutocompleteArrowDown(
    e,
    splitMatches,
    (value) => {
      splitQuery.value = value;
      getActiveTarget().Split = value;
    },
    classInput,
    'Split',
  );
}

function handleClassArrowDown(e) {
  handleAutocompleteArrowDown(
    e,
    classMatches,
    (value) => {
      classQuery.value = value;
      getActiveTarget().Class = value;
    },
    null,
    'Class',
  );
}

function searchNames() {
  const query = nameQuery.value ? nameQuery.value.trim().toLowerCase() : '';

  if (!query) {
    nameMatches.value = [];
    return;
  }

  // Filter, deduplicate, and map the matches
  const uniqueMatches = new Set();
  const filtered = names.value
    .filter((item) => {
      return item?.trim() !== '' && item?.toLowerCase().includes(query);
    })
    .filter((item) => {
      // Deduplicate by checking if we've already seen this exact value
      const lowerItem = item.toLowerCase();
      if (uniqueMatches.has(lowerItem)) {
        return false;
      }
      uniqueMatches.add(lowerItem);
      return true;
    })
    .map((item) => ({
      value: item,
      highlighted: highlightMatch(item, query),
    }));
  nameMatches.value = rankMatches(MRU_CATEGORIES.NAME, filtered, {
    getValue: (m) => m.value,
  });

  // Keep typed text; pick a match via click / ↓ / Tab
  getActiveTarget().Name = nameQuery.value;
  scheduleDropdownPosition();
}

function handleClassInput(e) {
  // Update typed query
  classQuery.value = e.target.value;
  dropdownAnchorEl.value = e.target;
  searchClasses();
}

/**
 * Split spreadsheet: Tab on Class commits the cell, moves focus immediately,
 * and saves the completed row in the background (do not wait on the API).
 * On the last child, also opens a new blank line and focuses Amount.
 * Shift+Tab returns to Account on the same row (no wrap).
 */
async function handleSiblingClassTabKey(e, index) {
  if (e.key !== 'Tab') return;
  e.preventDefault();
  e.stopPropagation();

  const sibling = siblings.value[index];
  if (!sibling) return;

  if (classMatches.value.length > 0) {
    const match = classMatches.value[0].value;
    classQuery.value = match;
    sibling.Class = match;
    recordFieldUse('Class', match);
  } else {
    sibling.Class = classQuery.value || sibling.Class || '';
  }
  classMatches.value = [];
  clearSearch();
  cancelBlurSave();

  if (e.shiftKey) {
    await nextTick();
    const row = e.target?.closest?.('.sibling-row');
    const accountEl = row?.querySelector?.('.col.account .cell-input');
    if (accountEl && typeof accountEl.focus === 'function') {
      accountEl.focus();
    }
    // Still on same row — no status banner spam; quiet save of current values
    enqueueSiblingSave(index, { syncParent: false, announce: false });
    return;
  }

  const isLast = index === siblings.value.length - 1;

  // Mark pending before focus moves so Class blur sees _savePending and skips
  markSiblingSavePending(index);

  // UI first — awaiting network made Tab feel multi-second laggy
  if (isLast) {
    await appendBlankSiblingAndFocusAmount();
  } else {
    await nextTick();
    focusSiblingAmount(index + 1);
    cancelBlurSave();
  }

  // Persist completed row without blocking focus / new-row creation
  // (spinner on this row shows until the queued save finishes)
  enqueueSiblingSave(index, { announce: true });
}

function searchClasses() {
  const query = classQuery.value.trim().toLowerCase();
  if (!query) {
    classMatches.value = [];
    return;
  }

  const tokens = query
    .split(',')
    .map((token) => token.trim())
    .filter((token) => token);

  let filteredClasses = classes.value;

  tokens.forEach((token) => {
    filteredClasses = filteredClasses.filter((item) =>
      item.toLowerCase().includes(token),
    );
  });

  // MRU first, then non-colon before colon (classSecondaryCompare)
  const ranked = rankMatches(MRU_CATEGORIES.CLASS, filteredClasses, {
    secondaryCompare: classSecondaryCompare,
  });

  classMatches.value = ranked.map((item) => ({
    value: item,
    highlighted: highlightMatch(item, query),
  }));

  // Keep typed text while filtering; apply selection on click/↓/Tab/blur.
  getActiveTarget().Class = classQuery.value;
  scheduleDropdownPosition();
}

function clearSearch() {
  accountMatches.value = [];
  classMatches.value = [];
  memoMatches.value = [];
  nameMatches.value = [];
  splitMatches.value = [];
  dropdownFixedStyle.value = {};
}

function selectMatch(match, field) {
  cancelBlurSave();
  const target = getActiveTarget();
  target[field] = match;
  recordFieldUse(field, match);

  if (field === 'Account') {
    accountQuery.value = match;
    accountMatches.value = [];
  } else if (field === 'Split') {
    splitQuery.value = match;
    splitMatches.value = [];
  } else if (field === 'Class') {
    classQuery.value = match;
    classMatches.value = [];
  } else if (field === 'Memo') {
    memoQuery.value = match;
    memoMatches.value = [];
  } else if (field === 'Name') {
    nameQuery.value = match;
    nameMatches.value = [];
  }

  // Non-split: advance focus to next field like before
  if (!isSplitMode.value) {
    let targetInput = null;
    if (field === 'Account') targetInput = splitInput;
    else if (field === 'Split') targetInput = classInput;
    else if (field === 'Name') targetInput = memoInput;
    else if (field === 'Memo') targetInput = accountInput;
    setTimeout(() => {
      if (targetInput?.value) targetInput.value.focus();
    }, 0);
  } else if (activeEdit.value?.kind === 'sibling') {
    // Autocomplete pick on a mid-row field: keep local only unless Class
    const idx = activeEdit.value.index;
    const field = activeEdit.value.field;
    const sibling = siblings.value[idx];
    if (sibling) sibling._dirty = true;
    if (field === 'Class') {
      markSiblingSavePending(idx);
      scheduleBlurSave(() =>
        persistSiblingRow(idx, { syncParent: true, announce: true }),
      );
    }
  } else if (activeEdit.value?.kind === 'parent') {
    markParentSavePending();
    scheduleBlurSave(() => persistParentRow());
  }
}
function validateInputs() {
  showErrorMessage('');
  if (!currentInput.Date) {
    showErrorMessage('Date is required.');
    return false;
  }
  if (!currentInput.Amount) {
    showErrorMessage('Amount is required.');
    return false;
  }
  if (!nameQuery.value && !isSplitMode.value) {
    showErrorMessage('Name is required.');
    return false;
  }
  if (!memoQuery.value) {
    showErrorMessage('Memo is required.');
    return false;
  }
  if (!accountQuery.value || accountQuery.value === '') {
    showErrorMessage('Account is required.');
    return false;
  }
  if (
    selectedRow.value?.index !== parentRowIndex &&
    isSplitMode.value &&
    (!classQuery.value || classQuery.value === '')
  ) {
    showErrorMessage('Class is required.');
    return false;
  }
  return true;
}

function editRow(rowToEdit, index, event) {
  if (event?.ctrlKey) {
    currentInput.Memo = rowToEdit.Memo;
    memoQuery.value = rowToEdit.Memo;
  } else {
    if (index !== parentRowIndex) selectRow(rowToEdit, index);
    // console.log('editRow: sibling', JSON.stringify(rowToEdit));
    Object.assign(currentInput, {
      Id: rowToEdit?.Id || null,
      Date: formatDate(rowToEdit ? rowToEdit.Date : parentEntry.value.Date),
      Num: rowToEdit?.index === parentRowIndex ? rowToEdit?.Num || '' : '',
      Type: rowToEdit?.index === parentRowIndex ? rowToEdit?.Type || '' : '',
      Amount: rowToEdit?.Amount || 0,
      Name: rowToEdit?.Name || '',
      Memo: rowToEdit?.Memo || '',
      Account: rowToEdit?.Account || '',
      Split: rowToEdit?.Split || '',
      Class: rowToEdit?.Class || '',
    });
    memoQuery.value = rowToEdit?.Memo || '';
    accountQuery.value = rowToEdit?.Account || '';
    classQuery.value = rowToEdit?.Class || '';
    splitQuery.value = rowToEdit?.Split || '';
    // set the focus to ref dateInput
    nextTick(() => {
      if (dateInput.value) {
        dateInput.value.focus();
      }
    });
  }
}

function selectRow(row, index) {
  selectedRow.value = { ...row, index };
}

/** Quiet save for blur / spreadsheet — no confirmation, keep UI open. */
async function saveEntryQuiet() {
  if (isSplitMode.value) {
    commitActiveSiblingFromQueries();
    await flushAllPendingSaves();
    // Only dirty / new-with-content children — not every sibling every time
    let result = { saved: 0, failed: 0 };
    await enqueueSave(async () => {
      result = await persistDirtySiblings({ syncParentIfNeeded: true });
    });
    await awaitSaveChain(CLOSE_FLUSH_MS);
    // Parent header fields may have changed without amount dirty flag
    if (parentEntry.value?.Id) {
      await enqueueSave(() => persistParentRow());
      await awaitSaveChain(CLOSE_FLUSH_MS);
    }
    if (result.failed > 0) {
      showErrorMessage(
        `Saved ${result.saved} split line(s); ${result.failed} failed. Retry Save before closing.`,
      );
    }
    return result;
  }

  // Sync autocomplete fields into currentInput
  currentInput.Name = nameQuery.value || currentInput.Name;
  currentInput.Memo = memoQuery.value || currentInput.Memo;
  currentInput.Account = accountQuery.value || currentInput.Account;
  currentInput.Split = splitQuery.value || currentInput.Split;
  currentInput.Class = classQuery.value || currentInput.Class;

  if (!currentInput.Date || currentInput.Amount === '' || currentInput.Amount == null) {
    return;
  }

  try {
    let mysqlFormattedDate = convertToMySQLFormat(currentInput.Date);
    if (!mysqlFormattedDate) return;

    const parentPayload = {
      Date: mysqlFormattedDate,
      Type: currentInput.Type,
      Num: currentInput.Num,
      Amount: formatAmount(currentInput.Amount) || 0.0,
      Name: currentInput.Name,
      Memo: currentInput.Memo,
      Account: currentInput.Account,
      Split: currentInput.Split,
      Class: currentInput.Class,
      IsSplitParent: false,
    };

    const id = currentInput.Id || parentEntry.value?.Id;
    if (id) {
      await axios.post('/api/update-ledger-entry', {
        Id: id,
        ...parentPayload,
      });
      Object.assign(parentEntry.value, parentPayload, { Id: id });
      Object.assign(currentInput, parentPayload, { Id: id });
      eventBus.emit('ledgerEntrySaved', id);
      flashSaved(`Entry #${id} saved.`);
    } else {
      // New row: only create once required fields look complete
      if (!validateInputs()) return;
      const response = await axios.put('/api/add-ledger-entry', parentPayload);
      if (response.status === 201) {
        const savedId = response.data.id;
        currentInput.Id = savedId;
        parentEntry.value.Id = savedId;
        eventBus.emit('ledgerEntrySaved', savedId);
        flashSaved(`Entry #${savedId} created.`);
      }
    }
  } catch (error) {
    console.error('Error quiet-saving ledger entry:', error);
    showErrorMessage('Failed to save ledger entry.');
  }
}

async function saveEntry() {
  console.log('saveEntry called');

  // Split spreadsheet mode: flush dirty rows + parent total, stay open
  if (isSplitMode.value) {
    await saveEntryQuiet();
    showErrorMessage('Saved (modified split lines + parent total).', true);
    return;
  }

  const inputsValid = validateInputs();
  if (!inputsValid) {
    return;
  }
  try {
    if (!currentInput.Date && isSplitMode.value) {
      currentInput.Date = parentEntry.value.Date;
    }

    let mysqlFormattedDate = convertToMySQLFormat(currentInput.Date);
    let response;

    const parentPayload = {
      Date: mysqlFormattedDate,
      Type: currentInput.Type,
      Num: currentInput.Num,
      Amount: formatAmount(currentInput.Amount) || 0.0,
      Name: currentInput.Name,
      Memo: currentInput.Memo,
      Account: currentInput.Account,
      Split: currentInput.Split,
      Class: currentInput.Class,
      IsSplitParent: false,
    };
    Object.assign(parentEntry.value, parentPayload);

    if (parentEntry.value.Id || currentInput.Id) {
      const id = parentEntry.value.Id || currentInput.Id;
      await axios.post('/api/update-ledger-entry', {
        Id: id,
        ...parentPayload,
      });
      eventBus.emit('ledgerEntrySaved', id);
    } else {
      response = await axios.put('/api/add-ledger-entry', parentPayload);
      if (response.status === 201) {
        const savedId = response.data.id;
        parentEntry.value.Id = savedId;
        currentInput.Id = savedId;
        eventBus.emit('ledgerEntrySaved', savedId);
      } else {
        throw new Error('Failed to create parent ledger entry');
      }
    }

    selectedRow.value = null;
    hideModal();
  } catch (error) {
    console.error('Error saving ledger entry:', error);
    showErrorModal('Failed to save ledger entry');
  }
}

function convertToMySQLFormat(dateStr) {
  if (!dateStr) return null;
  const s = String(dateStr).trim();

  const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{2})$/; // mm/dd/yy
  if (dateRegex.test(s)) {
    const [, month, day, year] = s.match(dateRegex);
    const fullYear = `20${year}`;
    return `${fullYear}-${month.padStart(2, '0')}-${day.padStart(2, '0')} 00:00:00`;
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.length > 10 ? s.slice(0, 19).replace('T', ' ') : `${s.slice(0, 10)} 00:00:00`;
  }

  const formatted = formatDate(s);
  if (formatted && dateRegex.test(formatted)) {
    return convertToMySQLFormat(formatted);
  }

  return null;
}

async function deleteEntry() {
  if (!currentId) {
    showErrorModal('No ledger entry selected to delete.');
    return;
  }
  openDeleteConfirmation();
}

async function duplicateEntry() {
  try {
    // Get the source data from parentEntry (existing entry) or currentInput
    const source = parentEntry.value.Id ? parentEntry.value : currentInput;

    if (!source.Date && !source.Name && !source.Amount) {
      showErrorModal('No entry data to duplicate.');
      return;
    }

    // Use today's date for the duplicate
    const todayFormatted = formatDate(new Date());
    const mysqlFormattedDate = convertToMySQLFormat(todayFormatted);

    // Build payload copying all fields from the current entry
    const duplicatePayload = {
      Date: mysqlFormattedDate,
      Type: source.Type || currentInput.Type || '',
      Num: PRINT_CHECK_NUMBER,
      Amount: formatAmount(source.Amount || currentInput.Amount || 0),
      Name: source.Name || currentInput.Name || '',
      Memo: source.Memo || currentInput.Memo || '',
      Account: source.Account || currentInput.Account || '',
      Split:
        source.Split === '-SPLIT-'
          ? ''
          : source.Split || currentInput.Split || '',
      Class: source.Class || currentInput.Class || '',
      IsSplitParent: false,
    };

    // Create the new entry via API
    const response = await axios.put('/api/add-ledger-entry', duplicatePayload);

    if (response.status === 201) {
      const newId = response.data.id;

      // Notify parent to refresh the ledger list
      eventBus.emit('ledgerEntrySaved', newId);

      // Now load the new entry into the current dialog for editing
      // Reset split mode since we duplicate as a single entry
      isSplitMode.value = false;
      siblings.value = [];
      selectedRow.value = null;

      // Fetch the newly created entry to populate all fields
      await fetchLedgerEntry(newId);

      currentInput.Num = PRINT_CHECK_NUMBER;
      parentEntry.value.Num = PRINT_CHECK_NUMBER;

      showErrorMessage(
        `Duplicated as entry #${newId}. Check number set to ${PRINT_CHECK_NUMBER}. Edit and save.`,
        true,
      );
    } else {
      throw new Error('Failed to duplicate ledger entry');
    }
  } catch (error) {
    console.error('Error duplicating ledger entry:', error);
    showErrorModal('Failed to duplicate ledger entry.');
  }
}

function formatDate(dateString) {
  if (!dateString) return '';

  // Ensure the date string is in a recognizable format, e.g., "YYYY-MM-DD"
  const date = new Date(dateString);

  // Check if the date object is valid
  if (isNaN(date.getTime())) {
    return ''; // Return empty if invalid date
  }

  // Extract month, day, and year
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
  const year = String(date.getFullYear()).slice(-2); // Get last two digits

  // Return formatted date as "mm/dd/yy"
  return `${month}/${day}/${year}`;
}

let saveAndCloseInFlight = false;

function commitSingleQueriesForSave() {
  if (activeEdit.value?.field === 'Amount') handleAmountBlur();
  if (activeEdit.value?.field === 'Date') validateAndFormatDate();
  currentInput.Name = nameQuery.value || currentInput.Name;
  currentInput.Memo = memoQuery.value || currentInput.Memo;
  currentInput.Account =
    accountMatches.value[0]?.value ||
    accountQuery.value ||
    currentInput.Account;
  currentInput.Split =
    splitMatches.value[0]?.value || splitQuery.value || currentInput.Split;
  currentInput.Class =
    classMatches.value[0]?.value || classQuery.value || currentInput.Class;
}

/** Ctrl-Enter: same save as the Save button, then close. Invalid input stays open. */
async function saveAndClose() {
  if (saveAndCloseInFlight || isClosing.value || showModal.value) return;
  saveAndCloseInFlight = true;
  try {
    cancelBlurSave();
    if (isSplitMode.value) {
      commitActiveSiblingFromQueries();
      const result = await saveEntryQuiet();
      if (result && result.failed > 0) return;
      if (!isClosing.value) await hideModal();
      return;
    }
    if (String(currentInput.Num || '').trim() === '>>') {
      await fetchNextNum();
    }
    commitSingleQueriesForSave();
    await saveEntry();
  } finally {
    saveAndCloseInFlight = false;
  }
}

function handleEnter(event) {
  if (ledgerSaveAndCloseKey(event)) return;
  // Split mode: Enter flushes like Save (row is complete enough to persist).
  if (isSplitMode.value) {
    if (activeEdit.value?.kind === 'sibling') {
      commitActiveSiblingFromQueries();
      const idx = activeEdit.value.index;
      enqueueSiblingSave(idx, { announce: true });
    } else if (activeEdit.value?.kind === 'parent') {
      markParentSavePending();
      enqueueSave(() => persistParentRow());
    } else {
      enqueueSave(() => saveEntryQuiet());
    }
    return;
  }
  saveEntryQuiet();
}

function handleModalKeyDown(event) {
  // Handle key events within the LedgerEntry modal and prevent propagation
  // This stops keyboard shortcuts in parent components from triggering

  // Allow arrow keys to propagate to input fields (for autocomplete functionality)
  if (
    event.key === 'ArrowDown' ||
    event.key === 'ArrowUp' ||
    event.key === 'ArrowLeft' ||
    event.key === 'ArrowRight'
  ) {
    // Don't stop propagation for arrow keys - let them reach input handlers
    return;
  }

  // Allow Tab key to propagate for form navigation
  if (event.key === 'Tab') {
    // Don't stop propagation for Tab - let input handlers manage it
    return;
  }

  if (ledgerSaveAndCloseKey(event)) {
    event.preventDefault();
    event.stopPropagation();
    saveAndClose();
    return;
  }

  // Allow Enter key to propagate for form handling
  if (event.key === 'Enter') {
    // Don't stop propagation for Enter - let input handlers manage it
    return;
  }

  event.stopPropagation();

  // Handle specific keys that should be contained within the modal
  if (event.key === 'Escape') {
    event.preventDefault();
    hideModal();
    return;
  }

  // Prevent other global keyboard shortcuts from propagating
  if (event.ctrlKey || event.altKey || event.metaKey) {
    // Allow certain combinations but prevent propagation
    if (
      event.key === 'a' ||
      event.key === 'c' ||
      event.key === 'v' ||
      event.key === 'x' ||
      event.key === 'z'
    ) {
      // Allow basic text editing shortcuts but prevent propagation
      event.stopPropagation();
      return;
    }
    // Prevent all other modifier key combinations from propagating
    event.preventDefault();
    event.stopPropagation();
    return;
  }
}

function preventGlobalKeyEvents(event) {
  const target = event.target;
  if (event.type === 'keydown' && ledgerSaveAndCloseKey(event)) {
    const inOtherModal =
      target &&
      target.closest &&
      target.closest('.modal') &&
      !target.closest('.ledger-entry-modal');
    if (!inOtherModal && !showModal.value) {
      event.preventDefault();
      event.stopPropagation();
      saveAndClose();
      return;
    }
  }
  // Never block typing in form controls — another dialog (e.g. ManageAddress
  // for check payee) may sit on top of Ledger Entry and shares document listeners.
  if (
    target &&
    (target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable)
  ) {
    // Still swallow events that are outside THIS modal so MainPage/QB shortcuts
    // don't fire — unless they belong to another modal dialog.
    const inOtherModal =
      target.closest &&
      target.closest('.modal') &&
      !target.closest('.ledger-entry-modal');
    if (inOtherModal) {
      return;
    }
    const ledgerModal = document.querySelector('.ledger-entry-modal');
    if (ledgerModal && ledgerModal.contains(target)) {
      if (
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'ArrowLeft' ||
        event.key === 'ArrowRight' ||
        event.key === 'Tab' ||
        event.key === 'Enter'
      ) {
        return;
      }
      event.stopPropagation();
      return;
    }
    // Form control outside any modal — still allow typing
    return;
  }

  // Check if the event originated from within the ledger entry modal
  const modalElement = document.querySelector('.ledger-entry-modal');
  if (modalElement && modalElement.contains(target)) {
    // Let navigation and autocomplete keys reach input handlers
    if (
      event.key === 'ArrowDown' ||
      event.key === 'ArrowUp' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight' ||
      event.key === 'Tab' ||
      event.key === 'Enter'
    ) {
      return;
    }

    // Event is from within the modal, allow it but prevent propagation beyond modal
    event.stopPropagation();
  } else if (target && target.closest && target.closest('.modal')) {
    // Another modal (ManageAddress, etc.) — do not steal its keys
    return;
  } else {
    // Event is from outside all modals, prevent it entirely when ledger modal is active
    event.preventDefault();
    event.stopPropagation();
  }
}
</script>

<style scoped>
.modal {
  font-size: 0.75em;
  font-family: calibri;
  position: fixed;
  top: 0;
  left: 0;
  width: 1318px;
  height: 100vh;
  background: rgba(0, 0, 0, 0.91);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  width: 1244px;
  position: absolute;
  left: 29px;
  background: white;
  border-radius: 4px;
  padding: 20px;
  box-shadow: 9px 8px 7px rgba(0, 0, 0, 0.1);
  margin-top: -180px;
}

.save-busy-overlay {
  position: absolute;
  inset: 0;
  z-index: 200;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(255, 255, 255, 0.72);
  border-radius: 4px;
  pointer-events: all;
  cursor: wait;
}

.save-busy-spinner {
  font-size: 2.2em;
  color: #675769;
}

.save-busy-label {
  font-size: 0.95em;
  font-weight: 600;
  color: #333;
  background: rgba(255, 255, 255, 0.9);
  padding: 6px 14px;
  border-radius: 6px;
  border: 1px solid #cfc3d4;
}

.close-btn:disabled,
.print-btn:disabled {
  opacity: 0.7;
  cursor: wait;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: -20px;
  margin-bottom: 11px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  background: #333;
  color: #fff;
  font-size: 1.2em;
  padding: 0;
  height: 37px;
  padding-left: 20px;
}

.table {
  display: flex;
  flex-direction: column;
}

.table-header,
.input-row,
.sibling-row,
.parent-row {
  display: flex;
  align-items: center;
  cursor: pointer;
}

.table-header {
  background: #ddd;
}

/* Even rows */
.sibling-row:nth-child(even) {
  background-color: rgb(226, 226, 226); /* Light gray */
}

/* Odd rows */
.sibling-row:nth-child(odd) {
  background-color: #ffffff; /* White or any other color of your choice */
}

.input-row {
  height: 26px;
  border-top: 2px solid blue;
  border-bottom: 2px solid blue;
  border-left: 0px;
  border-right: 0px;
}
.input-row input {
  border: none;
  background: #dccbeb;
  font-size: 9pt;
}

.special {
  margin-left: -5px;
  width: 100%;
  margin-right: -5px;
}

.col {
  flex: 1;
  padding: 5px;
  min-width: 0;
}

.col.date,
.col.num,
.col.type,
.col.amount,
.col.name,
.col.memo,
.col.split,
.col.account,
.col.class {
  text-align: left;
  min-height: 15px;
  border-right: 0;
}

.col.date {
  flex: 0 0 60px;
}

.col.type {
  flex: 0 0 40px;
}

.col.num {
  flex: 0 0 40px;
}

.col.amount {
  flex: 0 0 75px;
  text-align: right;
}

.col.name,
.col.memo,
.col.split {
  flex: 0 0 150px;
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.col.name {
  flex: 0 0 180px;
}

.col.split {
  flex: 0 0 75px;
}

.col.memo {
  flex: 0 0 315px;
}

.col.account {
  flex: 0 0 130px;
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.col.class {
  text-align: left;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex: 0 0 240px;
}

.modal-footer {
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  position: relative;
}

.modal-footer button {
  margin-top: 10px;
  border-radius: 5px;
  border: 1px solid #7b7070;
  background: #ad93b3;
  color: #fff;
  padding: 4px;
  width: 73px;
}

.modal-footer .refresh-btn {
  position: absolute;
  right: 0;
  width: 40px;
  background: #ad93b3;
  cursor: pointer;
}

.modal-footer .refresh-btn:hover {
  background: #8a7692;
}

.dropdown {
  position: absolute;
  background: #181818b5;
  border-top: 1px solid #bbb;
  border-left: 1px solid #bbb;
  max-height: 400px;
  overflow-y: auto;
  z-index: 10;
  color: white;
  font-size: 9pt;
  font-family: calibri, sans-serif;
  /* Scrollable without a visible scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.dropdown::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

/* Body-teleported panel: not clipped by .siblings-inner or the footer */
.dropdown.dropdown-portal {
  position: fixed;
  z-index: 10050;
  margin: 0;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
  font-size: 9pt;
  font-family: calibri, sans-serif;
  line-height: 1.25;
}

.dropdown.dropdown-account {
  min-width: 225px;
}

.dropdown.dropdown-memo {
  min-width: 280px;
}

.dropdown.dropdown-class {
  min-width: 220px;
}

.dropdown-item {
  padding: 5px;
  cursor: pointer;
  font-size: 9pt;
  font-family: calibri, sans-serif;
}

.dropdown-item:hover {
  background-color: #f0f0f0;
  color: #333;
}

:deep(.highlight) {
  background-color: yellow;
  font-weight: bold;
}

:deep(.highlight-term) {
  background-color: #b08fb8;
  color: #333;
  font-weight: bold;
}

.input-row input:focus {
  border: 0px;
  outline: none; /* Remove default outline */
  background-color: #f9f9ff; /* Optional: change background color on focus */
}

.close-btn {
  position: absolute;
  top: 5px;
  right: 10px;
  background: #333;
  color: white;
  border: 1px solid #888;
  border-radius: 5px;
  cursor: pointer;
  padding: 5px 10px;
  font-size: 0.7em;
  width: 30px;
  height: 25px;
}

.print-btn {
  position: absolute;
  top: 5px;
  right: 45px;
  background: #333;
  color: white;
  border: 1px solid #888;
  border-radius: 5px;
  cursor: pointer;
  padding: 5px 10px;
  font-size: 0.7em;
  width: 30px;
  height: 25px;
}

.selected-sibling-row {
  background-color: #e8dff0 !important;
}

.spreadsheet-row {
  cursor: default;
  min-height: 26px;
}

.spreadsheet-row .cell {
  position: relative;
  padding: 1px 2px;
  overflow: visible;
}

.cell-input {
  width: 100%;
  border: 1px solid transparent;
  background: transparent;
  font-size: 9pt;
  font-family: calibri, sans-serif;
  padding: 2px 3px;
  box-sizing: border-box;
  margin: 0;
}

.cell-input:focus {
  outline: none;
  border: 1px solid #6b4f8a;
  background: #f5f0fa;
  border-radius: 2px;
}

.amount-input {
  text-align: right;
}

.amount-readonly {
  font-size: 9pt;
  padding: 4px 5px;
  color: #444;
  user-select: none;
}

.parent-row.spreadsheet-row {
  background: #eef3f8;
  border-top: 2px solid #4a6fa5;
  border-bottom: 2px solid #4a6fa5;
  font-weight: 500;
}

.sibling-row.spreadsheet-row:hover {
  background-color: #f3eef7;
}

.row-save-pending {
  box-shadow: inset 3px 0 0 #c9a227;
}

.row-save-error {
  box-shadow: inset 3px 0 0 #c0392b;
}

.amount-with-status {
  display: flex;
  align-items: center;
  gap: 3px;
}

.amount-with-status .cell-input,
.amount-with-status.amount-readonly {
  flex: 1;
  min-width: 0;
}

.row-save-status {
  flex-shrink: 0;
  font-size: 0.75em;
  line-height: 1;
  width: 14px;
  text-align: center;
}

.row-save-status.pending {
  color: #b8860b;
}

.row-save-status.error {
  color: #c0392b;
}

.row-save-status.saved {
  color: #2e7d32;
  opacity: 0.75;
}

/* Always the same height whether empty, saving, saved, or error */
.ledger-status-slot {
  box-sizing: border-box;
  margin-top: 8px;
  height: 2.5em;
  flex-shrink: 0;
  overflow: hidden;
}

.ledger-status-inner {
  box-sizing: border-box;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  border: 1px solid transparent;
  border-radius: 4px;
  font-size: 0.85em;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  opacity: 0;
  pointer-events: none;
  background: transparent;
  color: transparent;
  transition: opacity 0.12s ease, background-color 0.12s ease,
    border-color 0.12s ease, color 0.12s ease;
}

.ledger-status-inner.is-pending {
  opacity: 1;
  pointer-events: auto;
  color: #6b5a00;
  background: #fff8e1;
  border-color: #f0d060;
}

.ledger-status-inner.is-pending i {
  color: #b8860b;
  flex-shrink: 0;
}

.ledger-status-inner.is-info {
  opacity: 1;
  pointer-events: auto;
  color: #141313;
  background: #a6dfa6;
  border-color: #7bc87b;
}

.ledger-status-inner.is-error {
  opacity: 1;
  pointer-events: auto;
  color: #fff;
  background: rgb(229, 109, 109);
  border-color: #c0392b;
}

.ledger-status-inner.is-empty {
  opacity: 0;
}
.emphasize-button {
  background-color: #7d6882 !important;
}

.siblings-outer {
  overflow: hidden;
  width: 1242px;
}

.siblings-inner {
  overflow-y: scroll;
  overflow-x: hidden;
  max-height: 286px;
  width: 1282px;
}
</style>
